/**
 * LA PALETA EN EL PLANO (F3, 2026-08-23) — «voy aventando elementos de cualquier tipo,
 * seleccionando la capa y el recinto» (Juan, 2026-08-22).
 *
 * Selector de capa (rubro) + lo que se captura en el recinto seleccionado:
 *   · Eléctrico: la paleta de E2 tal cual (contadores, aparatos, hallazgos con foto,
 *     nota) más «Colocar en el plano»: se elige el tipo y se toca el plano.
 *   · Smart, Iluminación, Madera y acero: contadores por producto del catálogo, con
 *     el mismo «Colocar». Cada toque escribe `cotizaciones.objetos[id]` por id
 *     determinista (`recinto__sku__propuesto`), como la eléctrica.
 *   · Riego, Cámaras, Wi-Fi: su motor sigue siendo la captura (hasta E7/E8); desde
 *     aquí se abre.
 *
 * La GUÍA DE DIAGNÓSTICO (capa Eléctrico, nivel casa): qué mirar primero y qué
 * capturar, en orden, con progreso. Las mediciones con instrumento NO están aquí:
 * son de la Revisión (el ingeniero) y de E6 (umbrales con fuente).
 */

import { useState } from 'react'
import PaletaElectrica, { TIPOS_ELECTRICO } from './Paleta.jsx'

export const RUBROS_PLANO = [
  { id: 'electrico',   nombre: 'Eléctrico',      corto: 'Eléc.' },
  { id: 'iluminacion', nombre: 'Iluminación',    corto: 'Ilum.' },
  { id: 'smart',       nombre: 'Smart home',     corto: 'Smart' },
  { id: 'taller',      nombre: 'Madera y acero', corto: 'Taller' },
  { id: 'riego',       nombre: 'Riego',          corto: 'Riego' },
  { id: 'camaras',     nombre: 'Cámaras',        corto: 'Cám.' },
  { id: 'wifi',        nombre: 'Wi-Fi',          corto: 'Wi-Fi' }
]
/** Rubros que se capturan aquí: del catálogo, o equipos que luego calcula su motor (E7-lite). */
export const RUBROS_CON_PALETA = ['electrico', 'iluminacion', 'smart', 'taller', 'riego', 'camaras', 'wifi']
export const RUBROS_CON_MOTOR = ['riego', 'camaras', 'wifi']

/** Etiqueta corta del marcador en el plano. */
export function etiquetaDe(o) {
  if (o.rubro === 'electrico') return ({ contacto: 'C', 'contacto-agua': 'Cg', apagador: 'A', 'salida-luz': 'L', escalera: 'Ae' })[o.tipo] || 'E'
  return (o.sku || '?').replace(/^(SH|TZ|MAD|SAL)-/, '').slice(0, 3)
}

const ahora = () => new Date().toISOString()

/** Pasos de la guía (nivel casa). Los derivados se calculan de lo capturado. */
export const PASOS_GUIA = [
  { id: 'medidor',   titulo: 'Medidor de CFE', donde: 'En la fachada o el terreno',
    campos: [{ id: 'servicio', etiqueta: 'Servicio', opciones: ['1 fase', '2 fases', '3 fases'] }, { id: 'varios', etiqueta: 'Medidores', opciones: ['Uno', 'Más de uno'] }] },
  { id: 'acometida', titulo: 'Acometida', donde: 'Del poste o la calle al medidor',
    campos: [{ id: 'tipo', etiqueta: 'Llega', opciones: ['Aérea', 'Subterránea'] }, { id: 'calibre', etiqueta: 'Cable', opciones: ['Se ve delgado', 'Normal', 'Grueso'] }] },
  { id: 'tablero',   titulo: 'Tablero', donde: 'Marca el servicio «Tablero» en el plano',
    campos: [{ id: 'principal', etiqueta: 'Interruptor principal', opciones: ['Sin principal', '30 A', '50 A', '70 A', '100 A', 'Más'] }, { id: 'espacios', etiqueta: 'Espacios libres', opciones: ['Sí', 'No'] }, { id: 'tierra', etiqueta: 'Tierra física', opciones: ['Se ve', 'No se ve', 'No sé'] }] },
  { id: 'recintos',  titulo: 'Recinto por recinto', donde: 'Contactos, apagadores, salidas de luz y aparatos', derivado: 'recintos' },
  { id: 'hallazgos', titulo: 'Hallazgos con foto', donde: 'Lo que se ve, se huele o se toca', derivado: 'hallazgos' },
  { id: 'revision',  titulo: 'Lo que exige instrumento', donde: 'La Revisión la hace el ingeniero', derivado: 'revision' }
]

export function progresoGuia(diagnostico = {}, resumen = {}) {
  let hechos = 0
  for (const p of PASOS_GUIA) {
    if (p.derivado === 'recintos') { if (resumen.recintosTotal && resumen.recintosConCaptura === resumen.recintosTotal) hechos++ }
    else if (p.derivado === 'hallazgos') { if (diagnostico.hallazgos?.hecho) hechos++ }
    else if (p.derivado === 'revision') { if (diagnostico.revision?.hecho) hechos++ }
    else if (diagnostico[p.id]?.hecho) hechos++
  }
  return { hechos, total: PASOS_GUIA.length }
}

function GuiaDiagnostico({ diagnostico = {}, onDiagnostico, resumen, onRevision }) {
  const [abierta, setAbierta] = useState(false)
  const { hechos, total } = progresoGuia(diagnostico, resumen)
  const poner = (paso, cambio) => onDiagnostico({ [paso]: { ...(diagnostico[paso] || {}), ...cambio, actualizadoEn: ahora() } })
  return (
    <div className="card guia">
      <button type="button" className="guia-cab" onClick={() => setAbierta(a => !a)} aria-expanded={abierta}>
        <span className="grow"><b>Guía de diagnóstico</b><span className="s"> · {hechos} de {total}</span></span>
        <span className="chev">{abierta ? '▾' : '›'}</span>
      </button>
      {abierta && PASOS_GUIA.map((p, i) => {
        const d = diagnostico[p.id] || {}
        const hecho = p.derivado === 'recintos' ? (resumen.recintosTotal > 0 && resumen.recintosConCaptura === resumen.recintosTotal) : !!d.hecho
        return (
          <div className={`guia-paso ${hecho ? 'hecho' : ''}`} key={p.id}>
            <div className="guia-num">{hecho ? '✓' : i + 1}</div>
            <div className="grow">
              <div className="t">{p.titulo}</div>
              <div className="s">{p.donde}</div>
              {p.campos?.map(c => (
                <div className="guia-campo" key={c.id}>
                  <span>{c.etiqueta}</span>
                  <div className="fila-pills">
                    {c.opciones.map(op => (
                      <button type="button" key={op} className={`ib sm ${d[c.id] === op ? 'pri' : ''}`}
                        onClick={() => poner(p.id, { [c.id]: op, hecho: true })}>{op}</button>
                    ))}
                  </div>
                </div>
              ))}
              {p.derivado === 'recintos' && <div className="s">{resumen.recintosConCaptura} de {resumen.recintosTotal} recintos con captura.</div>}
              {p.derivado === 'hallazgos' && (
                <div className="guia-campo"><span>{resumen.hallazgos} {resumen.hallazgos === 1 ? 'hallazgo' : 'hallazgos'} con foto</span>
                  <button type="button" className={`ib sm ${d.hecho ? 'pri' : ''}`} onClick={() => poner('hallazgos', { hecho: !d.hecho })}>{d.hecho ? 'Revisado' : 'Ya revisé la casa'}</button>
                </div>
              )}
              {p.derivado === 'revision' && (
                <div className="guia-campo">
                  {onRevision && <button type="button" className="ib sm" onClick={onRevision}>Agendar la Revisión</button>}
                  <button type="button" className={`ib sm ${d.hecho ? 'pri' : ''}`} onClick={() => poner('revision', { hecho: !d.hecho })}>{d.hecho ? 'Hecha' : 'Marcar hecha'}</button>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Contadores por producto del catálogo, con «Colocar». */
function PaletaGenerica({ rubro, recinto, productos, objetos, onObjeto, colocando, setColocando }) {
  const idDe = sku => `${recinto.id}__${sku}__propuesto`
  const poner = (p, q) => {
    const id = idDe(p.sku)
    const previo = objetos[id]
    if (q <= 0) { onObjeto(id, null); return }
    const posiciones = (previo?.posiciones || []).slice(0, q)
    onObjeto(id, { id, recinto: recinto.id, rubro, tipo: 'producto', sku: p.sku, nombre: p.nombre, estado: 'propuesto', q, posiciones, capturadoEn: previo?.capturadoEn || ahora() })
  }
  if (!productos.length) return <div className="empty chico">Sin productos de este rubro en el catálogo todavía.</div>
  return (
    <div className="paleta">
      {productos.map(p => {
        const q = objetos[idDe(p.sku)]?.q || 0
        const clave = `${rubro}:${p.sku}:propuesto`
        return (
          <div className="paleta-tipo" key={p.sku}>
            <div className="grow"><div className="t">{p.nombre}</div></div>
            <div className="contador">
              <button type="button" className="ib sm" aria-label={`Quitar ${p.nombre}`} disabled={!q} onClick={() => poner(p, q - 1)}>−</button>
              <b>{q}</b>
              <button type="button" className="ib sm" aria-label={`Agregar ${p.nombre}`} onClick={() => poner(p, q + 1)}>+</button>
              <button type="button" className={`ib sm colocar ${colocando === clave ? 'pri' : ''}`} aria-pressed={colocando === clave}
                title="Colocar en el plano" onClick={() => setColocando(colocando === clave ? null : clave)}>📍</button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function PaletaPlano({
  rubro, setRubro, recinto, cotId, objetos = {}, onObjeto,
  fotos = [], onFotos, nota = '', onNota, productosPorRubro = {},
  colocando, setColocando, onAbrirMotor, diagnostico, onDiagnostico, resumenGuia, onRevision
}) {
  const [estadoColocar, setEstadoColocar] = useState('propuesto')
  const def = RUBROS_PLANO.find(r => r.id === rubro) || RUBROS_PLANO[0]
  return (
    <div className="paleta-plano">
      <div className="sec">Capa</div>
      <div className="fila-pills capas-rubro" role="tablist">
        {RUBROS_PLANO.map(r => (
          <button type="button" key={r.id} role="tab" aria-selected={rubro === r.id} className={`ib sm ${rubro === r.id ? 'pri' : ''}`}
            onClick={() => { setRubro(r.id); setColocando(null) }}>{r.nombre}</button>
        ))}
      </div>

      {rubro === 'electrico' && (
        <GuiaDiagnostico diagnostico={diagnostico} onDiagnostico={onDiagnostico} resumen={resumenGuia} onRevision={onRevision} />
      )}

      {!recinto && <div className="empty chico">Elige un recinto en las capas para capturar {def.nombre.toLowerCase()} ahí.</div>}

      {recinto && RUBROS_CON_MOTOR.includes(rubro) && (
        <div className="card">
          <div className="t">Coloca los equipos en el plano; el cálculo lo hace {def.nombre}</div>
          <div className="s" style={{ marginBottom: 10 }}>
            {rubro === 'riego' ? 'Toca 📍 en una boquilla y luego el jardín por cada aspersor. Cobertura, zonas y partida se calculan al abrir Riego, ya con ellos puestos.'
              : rubro === 'camaras' ? 'Toca 📍 en un modelo y luego el predio por cada cámara. Orientación, alcance y partida se calculan al abrir Cámaras.'
              : 'Toca 📍 en un punto de acceso y luego el cuarto donde va. Cobertura y partida se calculan al abrir Wi-Fi.'}
          </div>
          <button type="button" className="ib pri" onClick={() => onAbrirMotor(rubro)}>Calcular en {def.nombre}</button>
        </div>
      )}

      {recinto && rubro === 'electrico' && (
        <>
          <div className="sec">{recinto.nombre} · colocar en el plano</div>
          <div className="card colocar-card">
            <div className="tabs" style={{ marginBottom: 8 }}>
              <button type="button" className={`tb ${estadoColocar === 'existente' ? 'on' : ''}`} onClick={() => setEstadoColocar('existente')}>Lo que hay</button>
              <button type="button" className={`tb ${estadoColocar === 'propuesto' ? 'on' : ''}`} onClick={() => setEstadoColocar('propuesto')}>Lo que se propone</button>
            </div>
            <div className="fila-pills">
              {TIPOS_ELECTRICO.map(t => {
                const clave = `electrico:${t.id}:${estadoColocar}`
                return <button type="button" key={t.id} className={`ib sm ${colocando === clave ? 'pri' : ''}`} aria-pressed={colocando === clave}
                  onClick={() => setColocando(colocando === clave ? null : clave)}>📍 {t.nombre}</button>
              })}
            </div>
            {colocando && <div className="s" style={{ marginTop: 8 }}>Toca el plano dentro de {recinto.nombre} por cada uno. Vuelve a tocar el tipo para dejar de colocar.</div>}
          </div>
          <div className="sec">{recinto.nombre} · contar</div>
          <PaletaElectrica recinto={recinto.id} nombre={recinto.nombre} cotId={cotId}
            objetos={objetos} onObjeto={onObjeto} fotos={fotos} onFotos={onFotos} nota={nota} onNota={onNota} />
        </>
      )}

      {recinto && rubro !== 'electrico' && RUBROS_CON_PALETA.includes(rubro) && (
        <>
          <div className="sec">{recinto.nombre} · {def.nombre.toLowerCase()}</div>
          {colocando && <div className="s" style={{ marginBottom: 8 }}>Toca el plano dentro de {recinto.nombre} por cada uno.</div>}
          <PaletaGenerica rubro={rubro} recinto={recinto} productos={productosPorRubro[rubro] || []}
            objetos={objetos} onObjeto={onObjeto} colocando={colocando} setColocando={setColocando} />
        </>
      )}
    </div>
  )
}
